import { apiClient } from '../utils/api';
import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export interface SendMessageRequest {
  chatbotId: string;
  sessionId: string;
  message: string;
  apiKey: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
  statusCode: number;
}

export interface MessageData {
  message: string;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  generationTimeMs?: number;
  confidence?: number;
}

export type MessageResponse = ApiResponse<MessageData>;

export interface Message {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

export interface StreamCallbacks {
  onToken: (token: string) => void;
  onDone: (meta: {
    tokens?: MessageData['tokens'];
    generationTimeMs?: number;
    confidence?: number;
    cached?: boolean;
    finalMessage?: string;
  }) => void;
  onError: (error: string) => void;
}

class MessageService {
  private async fetchStreamWithAuthRetry(
    url: string,
    data: SendMessageRequest,
    signal: AbortSignal
  ): Promise<Response> {
    const doFetch = (token?: string | null) =>
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
        signal,
      });

    let response = await doFetch(authService.getAccessToken());
    if (response.status !== 401) return response;

    const refreshed = await authService.refreshToken();
    const newToken = refreshed?.data?.accessToken;
    if (!refreshed?.success || !newToken) {
      throw new Error('Session expired. Please login again.');
    }

    response = await doFetch(newToken);
    return response;
  }

  async sendMessage(data: SendMessageRequest): Promise<MessageResponse> {
    const response = await apiClient.post<MessageResponse>('/api/v1/messages/send', data);
    return response;
  }

  /**
   * Send message via SSE stream. Falls back to standard sendMessage if streaming
   * endpoint is unavailable (404).
   * Returns an AbortController so the caller can cancel the stream.
   */
  sendMessageStream(
    data: SendMessageRequest,
    callbacks: StreamCallbacks
  ): AbortController {
    const controller = new AbortController();

    const run = async () => {
      try {
        const response = await this.fetchStreamWithAuthRetry(
          `${API_BASE_URL}/api/v1/messages/stream`,
          data,
          controller.signal
        );

        // If streaming not enabled, fall back to standard endpoint
        if (response.status === 404) {
          const fallback = await this.sendMessage(data);
          callbacks.onToken(fallback.data.message || '');
          callbacks.onDone({
            finalMessage: fallback.data.message || '',
            tokens: fallback.data.tokens,
            generationTimeMs: fallback.data.generationTimeMs,
            confidence: fallback.data.confidence,
          });
          return;
        }

        if (!response.ok) {
          const text = await response.text();
          callbacks.onError(text || `HTTP ${response.status}`);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          callbacks.onError('No response body');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';
        let streamClosed = false;

        const handleSseLine = (line: string) => {
          const normalizedLine = line.trimEnd();
          if (!normalizedLine) return;

          if (normalizedLine.startsWith('event: ')) {
            currentEvent = normalizedLine.slice(7).trim();
            return;
          }

          if (!normalizedLine.startsWith('data: ')) return;

          const rawData = normalizedLine.slice(6);
          if (currentEvent === 'token') {
            try {
              callbacks.onToken(JSON.parse(rawData));
            } catch {
              callbacks.onToken(rawData);
            }
          } else if (currentEvent === 'done') {
            streamClosed = true;
            try {
              callbacks.onDone(JSON.parse(rawData));
            } catch {
              callbacks.onDone({});
            }
          } else if (currentEvent === 'error') {
            streamClosed = true;
            try {
              const parsed = JSON.parse(rawData);
              callbacks.onError(parsed.message || rawData);
            } catch {
              callbacks.onError(rawData);
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            handleSseLine(line);
          }
        }

        // Flush any trailing buffered SSE content (e.g. final "done" without newline).
        if (buffer.trim()) {
          const trailingLines = buffer.split('\n');
          trailingLines.forEach(handleSseLine);
        }

        // Ensure UI exits loading state when stream ends unexpectedly without explicit done/error.
        if (!streamClosed && !controller.signal.aborted) {
          callbacks.onDone({});
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          callbacks.onError(err.message || 'Stream failed');
        }
      }
    };

    run();
    return controller;
  }
}

const messageService = new MessageService();
export default messageService;

export { MessageService };
